import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_GROUPS, SHADOW } from "../../../theme/tokens";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import { CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { HolidayDayPartHoursRow } from "./HolidayDayPartHoursRow";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HOLIDAY_DAY_PARTS, type JobDayPartHours } from "./holidayApi";

/** The three day parts share the width evenly; the job name takes what's left after the actions. */
const JOB_COLUMN_WIDTH = "22%";
const PART_COLUMN_WIDTH = "26%";

type Props = { jobs: JobDayPartHours[]; canEdit: boolean };

/** Each job's Day, Evening and Night hours — what a shift's start time is matched against. */
export const HolidayDayPartHoursSection: FC<Props> = ({ jobs, canEdit }) => {
	const { t } = useTranslation();
	return (
		<SettingsSection
			title={t("holidays.dayParts.title")}
			icon={<ScheduleOutlinedIcon />}
			description={t("holidays.dayParts.description")}
			layout="stack"
			dataTestId="holiday-day-parts-section"
			sx={{ boxShadow: SHADOW.PANEL_SOFT }}
		>
			{jobs.length === 0 ? (
				<EmptyState icon={<ScheduleOutlinedIcon />} title={t("holidays.dayParts.noJobs")} dataTestId="holiday-day-parts-empty" />
			) : (
				<Box sx={CONFIG_TABLE_CONTAINER_SX}>
					{/* Fixed widths, so a row opening its pickers doesn't push the other columns around. */}
					<Table size="small" data-testid="holiday-day-parts-table" sx={{ tableLayout: "fixed", minWidth: HOLIDAY_GROUPS.DAY_PARTS_TABLE_MIN_WIDTH_PX }}>
						<TableHead>
							<TableRow>
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: JOB_COLUMN_WIDTH }]}>{t("holidays.dayParts.column.job")}</TableCell>
								{HOLIDAY_DAY_PARTS.map((dayPart) => (
									<TableCell key={dayPart} sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: PART_COLUMN_WIDTH }]}>
										<HolidayDayPartLabel dayPart={dayPart} sx={{ color: "inherit", fontWeight: "inherit" }} />
									</TableCell>
								))}
								<TableCell
									sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: HOLIDAY_GROUPS.DAY_PARTS_ACTIONS_WIDTH_PX }]}
									aria-label={t("holidays.list.column.actions")}
								/>
							</TableRow>
						</TableHead>
						<TableBody>
							{jobs.map((job) => (
								<HolidayDayPartHoursRow key={job.jobId} job={job} canEdit={canEdit} />
							))}
						</TableBody>
					</Table>
				</Box>
			)}
			<InlineHint tone="info" dataTestId="holiday-day-parts-hint">
				{t("holidays.dayParts.hint")}
			</InlineHint>
		</SettingsSection>
	);
};
